import React, { useRef, useState } from 'react';
import { Form, Button, Container, Header, Segment, Message } from 'semantic-ui-react';
import { useToast } from './utils/Toast';
import { useTheme } from './ThemeContext';
import './AuthPages.css';
import TurnstileWidget, { TurnstileHandle } from './components/TurnstileWidget';
import { requestMagicLink, ApiError } from './api/auth';
import { 
  isMockAuthEnabled, 
  getMockAccount, 
  createMockSession, 
  saveMockSession,
  MOCK_ACCOUNTS 
} from './utils/mockAuth';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { showToast } = useToast();
  const theme = useTheme();
  const turnstileRef = useRef<TurnstileHandle>(null);
  
  const useMockAuth = isMockAuthEnabled();

  const handleMockLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const mockAccount = getMockAccount(normalizedEmail);

      if (!mockAccount) {
        showToast('Mock account not found. Available: admin@example.com, admin_user@example.com, user@example.com', 'error');
        setLoading(false);
        return;
      }

      // Create and save mock session
      const mockSession = createMockSession(mockAccount);
      saveMockSession(mockSession);

      showToast(`Logged in as ${mockAccount.fullName} (${mockAccount.role})`, 'success');
      
      // Navigate based on role and force reload to pick up session
      setTimeout(() => {
        if (mockAccount.role === 'admin') {
          window.location.href = '/dashboard';
        } else if (mockAccount.role === 'admin_client') {
          window.location.href = '/manager-dashboard';
        } else {
          window.location.href = '/user-dashboard';
        }
      }, 300);
    } catch (err) {
      showToast('Mock login failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      if (!normalizedEmail) {
        showToast('Enter a valid email address.', 'warning');
        setLoading(false);
        return;
      }

      if (!turnstileRef.current) {
        throw new Error('Captcha unavailable. Please reload the page.');
      }

      let captchaToken: string;

      try {
        captchaToken = await turnstileRef.current.execute();
      } catch (captchaErr) {
        console.error('[Login] Captcha execution failed:', captchaErr);
        throw new Error('Captcha verification failed. Please try again in a moment.');
      }

      await requestMagicLink({
        email: normalizedEmail,
        captchaToken
      });

      showToast('A sign in link was sent to your email.', 'success');
      setEmail(normalizedEmail);
      setSubmitted(true);
      turnstileRef.current?.reset();
    } catch (err) {
      let message = 'Unable to send link right now.';

      if (err instanceof ApiError) {
        const detailMessage =
          typeof err.details === 'object' && err.details && 'details' in err.details
            ? (err.details as { details?: unknown }).details
            : null;

        if (typeof detailMessage === 'string' && detailMessage.toLowerCase().includes('signups not allowed for otp')) {
          message = 'Email not found.';
        } else {
          message = err.message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }

      showToast(message, 'error');
      setSubmitted(false);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = theme === 'dark'
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  return (
    <div className="auth-container">
      <Container text>
        <div className="auth-card">
          <div className="auth-logo-container">
            <img src={logoSrc} alt="Pilbara Risk Solutions" className="auth-logo" />
          </div>
          <Header as="h2" textAlign="center" className="auth-subheader">
            Sign In
          </Header>
          
          {useMockAuth && (
            <Message info>
              <Message.Header>Mock Authentication Enabled</Message.Header>
              <Message.List>
                {MOCK_ACCOUNTS.map(acc => (
                  <Message.Item key={acc.email}>
                    <strong>{acc.email}</strong> - {acc.fullName} ({acc.role === 'admin' ? 'Admin' : acc.role === 'admin_client' ? 'Manager' : 'User'})
                  </Message.Item>
                ))}
              </Message.List>
            </Message>
          )}
          
          <Segment raised className="auth-segment">
            {submitted === false ? (
              <Form onSubmit={useMockAuth ? handleMockLogin : handleSubmit}>
                <Form.Field>
                  <label>Email</label>
                  <Form.Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    icon="mail"
                    iconPosition="left"
                  />
                </Form.Field>

                {!useMockAuth && (
                  <TurnstileWidget
                    ref={turnstileRef}
                    appearance="execute"
                    size="invisible"
                    className="turnstile-hidden"
                  />
                )}

                <Button
                  type="submit"
                  primary
                  fluid
                  size="large"
                  loading={loading}
                  disabled={loading || !email.trim()}
                  className="auth-button"
                >
                  Sign In
                </Button>
              </Form>
            ) : (
              <div className="auth-confirmation">
                <Header as="h3" textAlign="center" className="auth-confirmation-title">
                  Check your inbox
                </Header>
                <p className="auth-confirmation-text">
                  A sign in link was sent to your email. Use it to continue to your dashboard.
                </p>
                <p className="auth-confirmation-email">
                  <strong>{email}</strong>
                </p>
                <p className="auth-confirmation-subtext">
                  Didn&apos;t receive it? Refresh this page to request another link.
                </p>
              </div>
            )}
          </Segment>
        </div>
      </Container>
    </div>
  );
};

export default Login;